/**
 * GlobalSettingsCard - Global settings for all Batch Matrix rows
 *
 * Collapsible card with mode selector, template/collection/prompt config,
 * and generation settings (aspect ratios, variants, seed).
 */

import { useState } from 'react';
import { ChevronDown, Info, FileText, Layers, MessageSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Select } from '@/components/v2/ui/Select';
import { Button } from '@/components/v2/ui/Button';
import { GenerationSettingsControls } from './GenerationSettingsControls';
import { cn } from '@/lib/utils';
import type { BatchMode } from '@/lib/api/batch';
import type { GlobalMatrixSettings } from '../matrix-utils';

interface Template {
  id: string;
  name: string;
}

interface Collection {
  id: string;
  name: string;
}

export interface GlobalSettingsCardProps {
  globalSettings: GlobalMatrixSettings;
  templates: Template[];
  collections: Collection[];
  onSettingsChange: (key: string, value: string | number | boolean | string[]) => void;
  onApplyToAll: () => void;
  totalRowsCount: number;
  disabled?: boolean;
}

const MODE_OPTIONS: Array<{ value: BatchMode; label: string; icon: typeof FileText }> = [
  { value: 'template', label: 'Template', icon: FileText },
  { value: 'collection', label: 'Collection', icon: Layers },
  { value: 'prompt', label: 'Manuell', icon: MessageSquare },
];

export function GlobalSettingsCard({
  globalSettings,
  templates,
  collections,
  onSettingsChange,
  onApplyToAll,
  totalRowsCount,
  disabled = false,
}: GlobalSettingsCardProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const isCollectionMode = globalSettings.mode === 'collection';

  return (
    <div className={cn('rounded-lg border-2 p-4 transition-all', 'border-primary-400 bg-primary-50/50 dark:border-primary-700 dark:bg-primary-950/20')}>
      <button type="button" onClick={() => setIsExpanded(!isExpanded)} className="w-full flex items-center justify-between mb-3 hover:opacity-80 transition-opacity">
        <h3 className="text-sm font-medium text-primary-700 dark:text-primary-300">Globale Einstellungen</h3>
        <ChevronDown className={cn('h-5 w-5 text-primary-600 dark:text-primary-400 transition-transform', isExpanded && 'rotate-180')} />
      </button>

      {isExpanded && (
        <div className="space-y-4 animate-in fade-in duration-200">
          {/* Mode Selector */}
          <div className="flex gap-1 p-1 bg-neutral-100 dark:bg-neutral-800 rounded-lg">
            {MODE_OPTIONS.map(({ value, label, icon: Icon }) => (
              <button key={value} type="button" onClick={() => onSettingsChange('mode', value)} disabled={disabled}
                className={cn('flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all', globalSettings.mode === value ? 'bg-white dark:bg-neutral-700 text-orange-600 dark:text-orange-400 shadow-sm' : 'text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300', disabled && 'opacity-50 cursor-not-allowed')}>
                <Icon className="h-3.5 w-3.5" />
                {label}
              </button>
            ))}
          </div>

          {/* Mode-specific content */}
          {globalSettings.mode === 'template' && (
            <div className="space-y-2">
              <label className="text-xs font-medium text-neutral-700 dark:text-neutral-300">Template</label>
              <Select
                options={[{ value: '__none__', label: t('batch.templateChoose') }, ...templates.map((tmpl) => ({ value: tmpl.id, label: tmpl.name }))]}
                value={globalSettings.templateId || '__none__'}
                onValueChange={(v) => onSettingsChange('templateId', v === '__none__' ? '' : v)}
                disabled={disabled}
              />
            </div>
          )}

          {globalSettings.mode === 'collection' && (
            <div className="space-y-2">
              <label className="text-xs font-medium text-neutral-700 dark:text-neutral-300">Collection</label>
              <Select
                options={[{ value: '__none__', label: t('batch.collectionChoose') }, ...collections.map((c) => ({ value: c.id, label: c.name }))]}
                value={globalSettings.collectionId || '__none__'}
                onValueChange={(v) => onSettingsChange('collectionId', v === '__none__' ? '' : v)}
                disabled={disabled}
              />
            </div>
          )}

          {globalSettings.mode === 'prompt' && (
            <div className="space-y-2">
              <label className="text-xs font-medium text-neutral-700 dark:text-neutral-300">Prompt</label>
              <textarea
                value={globalSettings.customPrompt || ''}
                onChange={(e) => onSettingsChange('customPrompt', e.target.value)}
                disabled={disabled}
                placeholder={t('batch.globalPromptPlaceholder')}
                className="w-full px-3 py-2 rounded-lg border text-sm resize-vertical min-h-[60px] bg-white dark:bg-neutral-800 border-neutral-200 dark:border-neutral-600 placeholder:text-neutral-400"
              />
            </div>
          )}

          {/* Generation Settings (hidden for collection mode) */}
          <GenerationSettingsControls
            aspectRatios={globalSettings.aspectRatios}
            variants={globalSettings.variants}
            seedMode={globalSettings.seedMode}
            onAspectRatiosChange={(v) => onSettingsChange('aspectRatios', v)}
            onVariantsChange={(v) => onSettingsChange('variants', v)}
            onSeedModeChange={(v) => onSettingsChange('seedMode', v)}
            disabled={disabled}
            hideAspectRatios={isCollectionMode}
            hideVariants={isCollectionMode}
          />

          {/* Apply Button */}
          <Button variant="warning" onClick={onApplyToAll} disabled={disabled || totalRowsCount === 0} className="w-full">
            Auf alle anwenden ({totalRowsCount} Zeilen)
          </Button>

          <div className="flex items-start gap-2 text-xs text-primary-700 dark:text-primary-300">
            <Info className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{t('batch.newRowsHint')}</span>
          </div>
        </div>
      )}
    </div>
  );
}
